// ======================================================================
// insightsService.js — Processamento REAL para Emotion, Keywords, Riscos e Tópicos
// ======================================================================
//
// Este módulo oferece 4 funções principais:
//
// 1) extractEmotionStats(messages)
// 2) extractKeywords(messages)
// 3) detectRiskPatterns(messages)
// 4) extractTopics(messages)
//
// Todas recebem mensagens no formato:
// { texto: string, de: "user" | "admin", origem?, timestamp? }
//
// ======================================================================

// -----------------------------------------------------
// 🔥 HELPERS BÁSICOS
// -----------------------------------------------------

function normalize(text) {
  if (!text) return "";
  return text
    .toLowerCase()
    .normalize("NFD") // remove acentos
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s]/g, " "); // remove pontuação
}

function tokenize(text) {
  return normalize(text).split(/\s+/).filter((w) => w.length >= 3);
}

// -----------------------------------------------------
// ❤️ 1. EMOTION ANALYSIS — análise emocional simples
// -----------------------------------------------------
//
// Algoritmo baseado em dicionários de palavras
// Pode ser expandido futuramente com modelos reais de NLP
//
// -----------------------------------------------------

const EMOTION_WORDS = {
  alegria: [
    "feliz",
    "alegr",
    "animad",
    "gratid",
    "empolgado",
    "aliviad",
    "contente"
  ],
  tristeza: [
    "triste",
    "chorar",
    "depress",
    "sofr",
    "desanimado",
    "saudade",
    "carente"
  ],
  medo: ["medo", "assustada", "ansiosa", "receio", "insegurança", "pavor"],
  raiva: ["raiva", "ódio", "irritad", "bravo", "nervoso", "impaciente"],
  amor: [
    "amo",
    "amor",
    "apaixon",
    "querer bem",
    "carinho",
    "afeto",
    "cuidar"
  ]
};

export function extractEmotionStats(messages) {
  const score = {
    alegria: 0,
    tristeza: 0,
    medo: 0,
    raiva: 0,
    amor: 0
  };

  messages.forEach((msg) => {
    const text = normalize(msg.texto);
    if (!text) return;

    Object.entries(EMOTION_WORDS).forEach(([emotion, words]) => {
      words.forEach((w) => {
        if (text.includes(w)) score[emotion]++;
      });
    });
  });

  const total =
    score.alegria + score.tristeza + score.medo + score.raiva + score.amor;

  if (total === 0) return score;

  // transforma em porcentagem
  Object.keys(score).forEach((k) => {
    score[k] = (score[k] / total) * 100;
  });

  return score;
}

// -----------------------------------------------------
// ☁️ 2. KEYWORDS CLOUD — contagem simples de palavras
// -----------------------------------------------------

export function extractKeywords(messages) {
  const count = {};

  messages.forEach((msg) => {
    tokenize(msg.texto).forEach((word) => {
      if (!count[word]) count[word] = 0;
      count[word]++;
    });
  });

  const sorted = Object.entries(count)
    .map(([word, weight]) => ({ word, weight }))
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 50);

  return sorted;
}

// -----------------------------------------------------
// ⚠️ 3. RISK DETECTION — alerta de padrões sensíveis
// -----------------------------------------------------

const RISK_TERMS = {
  "Relato de violência": [
    "apanhei",
    "bater",
    "soco",
    "violencia",
    "agressao",
    "machucar"
  ],
  "Sinais de depressão": [
    "nao aguento mais",
    "acabar com tudo",
    "triste demais",
    "sem vontade",
    "desesperado"
  ],
  "Relacionamento abusivo": [
    "controle",
    "ciume excessivo",
    "manipul",
    "toxica",
    "abusivo"
  ]
};

export function detectRiskPatterns(messages) {
  const risks = [];

  messages.forEach((msg) => {
    const text = normalize(msg.texto);

    Object.entries(RISK_TERMS).forEach(([riskType, patterns]) => {
      patterns.forEach((p) => {
        if (text.includes(p)) {
          risks.push({
            type: riskType,
            sample: msg.texto
          });
        }
      });
    });
  });

  return risks;
}

// -----------------------------------------------------
// 📈 4. TOPICS TREND — tópicos mais discutidos
// -----------------------------------------------------

const TOPIC_GROUPS = {
  "Relacionamento": [
    "namoro",
    "relacao",
    "companheiro",
    "parceiro",
    "saudade"
  ],
  "Ansiedade": ["ansiedade", "nervoso", "pressao", "inseguranca"],
  "Autoestima": ["autoestima", "confiante", "seguranca"],
  "Traição": ["traicao", "corno", "trair", "enganar"],
  "Reconquista": ["voltar", "reconquistar", "segunda chance"]
};

export function extractTopics(messages) {
  const counts = {};

  messages.forEach((msg) => {
    const text = normalize(msg.texto);

    Object.entries(TOPIC_GROUPS).forEach(([topic, keywords]) => {
      keywords.forEach((k) => {
        if (text.includes(k)) {
          if (!counts[topic]) counts[topic] = 0;
          counts[topic]++;
        }
      });
    });
  });

  return Object.entries(counts)
    .map(([topic, count]) => ({ topic, count }))
    .sort((a, b) => b.count - a.count);
}
